import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import FolderInstance from "../../api/FolderApi";
import { FolderTypeEnum, IFolder } from "../../types/models";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { pruneDeletedMedia } from "./MediaSlice";
import { refreshStreamSnapshot } from "../refreshStreamSnapshot";

interface FolderState {
  loading: boolean;
  folders: IFolder[];
  folderById?: IFolder | null;
  addItemsToFolderLoading: boolean;
  streamId: string | null;
  folderType: FolderTypeEnum | null;
  requestId: string | null;
  error: string | null;
}

const initialState: FolderState = {
  folders: [],
  folderById: null,
  loading: false,
  addItemsToFolderLoading: false,
  streamId: null,
  folderType: null,
  requestId: null,
  error: null,
};

export const folderSlice = createSlice({
  name: "folder",
  initialState,
  reducers: {
    setFolders(state, action: PayloadAction<IFolder[]>) {
      state.folders = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    beginFoldersRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        type: FolderTypeEnum;
        requestId: string;
        background?: boolean;
      }>,
    ) {
      if (
        state.streamId !== action.payload.streamId ||
        state.folderType !== action.payload.type
      )
        state.folders = [];
      state.streamId = action.payload.streamId;
      state.folderType = action.payload.type;
      state.requestId = action.payload.requestId;
      state.error = null;
      state.loading = !action.payload.background;
    },
    failFoldersRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        type: FolderTypeEnum;
        requestId: string;
        error: string;
      }>,
    ) {
      if (
        state.streamId === action.payload.streamId &&
        state.folderType === action.payload.type &&
        state.requestId === action.payload.requestId
      )
        state.error = action.payload.error;
    },
    completeFoldersRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        type: FolderTypeEnum;
        requestId: string;
        folders: IFolder[];
      }>,
    ) {
      if (
        state.streamId === action.payload.streamId &&
        state.folderType === action.payload.type &&
        state.requestId === action.payload.requestId
      )
        state.folders = action.payload.folders;
    },
    finishFoldersRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        type: FolderTypeEnum;
        requestId: string;
      }>,
    ) {
      if (
        state.streamId === action.payload.streamId &&
        state.folderType === action.payload.type &&
        state.requestId === action.payload.requestId
      ) {
        state.loading = false;
        state.requestId = null;
      }
    },
    setFolderById(state, action: PayloadAction<IFolder | undefined | null>) {
      state.folderById = action.payload;
    },
    setAddItemsToFolderLoading(state, action: PayloadAction<boolean>) {
      state.addItemsToFolderLoading = action.payload;
    },
    onFolderNameChange(
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) {
      const index = state.folders.findIndex(
        (folder) => folder.id === action.payload.id
      );
      if (index !== -1) {
        state.folders[index].name = action.payload.name;
      }
      if (state.folderById?.id === action.payload.id) {
        state.folderById.name = action.payload.name;
      }
    },
    onFolderDelete(state, action: PayloadAction<string>) {
      const index = state.folders.findIndex(
        (folder) => folder.id === action.payload
      );
      if (index !== -1) {
        state.folders.splice(index, 1);
      }
    },
    onFolderCreate(state, action: PayloadAction<IFolder>) {
      state.folders.push(action.payload);
    },
  },
});

export const {
  setLoading,
  beginFoldersRequest,
  completeFoldersRequest,
  failFoldersRequest,
  finishFoldersRequest,
  setAddItemsToFolderLoading,
  setFolderById,
  setFolders,
  onFolderNameChange,
  onFolderDelete,
  onFolderCreate,
} = folderSlice.actions;

export const getFolders = createAsyncThunk(
  "folder/getFolders",
  async (
    {
      streamId,
      type,
      background = false,
    }: {
      streamId: string;
      type: FolderTypeEnum;
      background?: boolean;
    },
    { dispatch, getState, requestId },
  ) => {
    dispatch(beginFoldersRequest({ streamId, type, requestId, background }));
    try {
      const response = await FolderInstance.getFolders(streamId, type);
      dispatch(
        completeFoldersRequest({
          streamId,
          type,
          requestId,
          folders: response.data,
        }),
      );
    } catch (err: any) {
      dispatch(
        failFoldersRequest({
          streamId,
          type,
          requestId,
          error:
            err?.response?.data?.message ||
            err?.message ||
            "Unable to load folders",
        }),
      );
      const state = (getState() as { folder: FolderState }).folder;
      if (
        !background &&
        state.streamId === streamId &&
        state.folderType === type &&
        state.requestId === requestId
      )
        dispatchErrorNotification(err);
    } finally {
      dispatch(finishFoldersRequest({ streamId, type, requestId }));
    }
  }
);

export const createFolder = createAsyncThunk(
  "folder/createFolder",
  async (
    { streamId, folder }: { streamId: string; folder: Pick<IFolder, "name" | "type"> },
    { dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      const response = await FolderInstance.createFolder(streamId, folder);
      dispatch(onFolderCreate(response.data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const updateFolder = createAsyncThunk(
  "folder/updateFolder",
  async (
    { streamId, folder }: { streamId: string; folder: Pick<IFolder, "id" | "name"> },
    { dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      await FolderInstance.updateFolder(streamId, folder);
      dispatch(onFolderNameChange(folder));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const getFolderById = createAsyncThunk(
  "folder/getFolderById",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await FolderInstance.getFolderById(streamId, id);
      dispatch(setFolderById(response.data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const deleteFolder = createAsyncThunk(
  "folder/deleteFolder",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      // The API deletes the folder's media too; drop them from every stream list afterwards.
      const { data: folder } = await FolderInstance.getFolderById(streamId, id);
      const memberIds = [...(folder.visualMedias ?? []), ...(folder.audios ?? [])].map((m) => m.id);
      await FolderInstance.deleteFolder(streamId, id);
      dispatch(onFolderDelete(id));
      if (memberIds.length) dispatch(pruneDeletedMedia(memberIds));
      dispatch(refreshStreamSnapshot(streamId));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);

export const addItemsToFolder = createAsyncThunk(
  "folder/addItemsToFolder",
  async (
    {
      streamId,
      data,
    }: {
      streamId: string;
      data: { folderId: string | null; itemIds: string[]; type: FolderTypeEnum };
    },
    { dispatch }
  ) => {
    dispatch(setLoading(true));
    try {
      await FolderInstance.addItemsToFolder(streamId, data);
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setLoading(false));
  }
);
